import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { ThemeProvider as SCThemeProvider } from 'styled-components';

const STORAGE_KEY = 'ga-theme-preference';
const RESOLVED_COOKIE = 'ga-theme-resolved';
const COOKIE_MAX_AGE = 31536000; // 1 year

// Theme preference types
export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedThemeMode = 'light' | 'dark';

// Spacing scale
const spacing = {
  xxs: '4px',
  xs: '8px',
  sm: '12px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
  xxxl: '64px',
} as const;

const typography = {
  fontFamily: '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontFamilyDisplay: '"Libre Baskerville", "Georgia", "Times New Roman", serif',
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    md: '1.0625rem',
    lg: '1.1rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '2rem',
    '4xl': '2.5rem',
  },
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    bold: 700,
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
};

const radius = {
  sm: '4px',
  md: '8px',
  lg: '16px',
  xl: '24px',
  full: '50rem',
};

const breakpoints = {
  mobile: '375px',
  sm: '576px',
  tablet: '768px',
  desktop: '1024px',
  wide: '1280px',
};

const shadows = {
  subtle: '0 1px 3px rgba(0, 0, 0, 0.08)',
  card: '0 2px 8px rgba(0, 0, 0, 0.1)',
  elevated: '0 4px 16px rgba(0, 0, 0, 0.12)',
  overlay: '0 8px 32px rgba(0, 0, 0, 0.18)',
};

const darkShadows = {
  subtle: '0 1px 3px rgba(0, 0, 0, 0.3)',
  card: '0 2px 8px rgba(0, 0, 0, 0.4)',
  elevated: '0 4px 16px rgba(0, 0, 0, 0.5)',
  overlay: '0 8px 32px rgba(0, 0, 0, 0.6)',
};

export interface ThemeColors {
  [key: string]: string;
  primary: string;
  primaryHover: string;
  accent: string;
  accentHover: string;
  background: string;
  backgroundAlt: string;
  backgroundElevated: string;
  surface: string;
  surfaceHover: string;
  text: string;
  textSecondary: string;
  textMuted: string;
  textInverse: string;
  border: string;
  borderLight: string;
  divider: string;
  headerBg: string;
  headerBorder: string;
  headerShadow: string;
  searchBg: string;
  searchBorder: string;
  searchFocusBg: string;
  footerBgStart: string;
  footerBgEnd: string;
  footerBorder: string;
  footerText: string;
  footerHeading: string;
  footerCopyright: string;
  cardBg: string;
  cardBorder: string;
  inputBg: string;
  inputBorder: string;
  inputText: string;
  menuBg: string;
  menuBorder: string;
  menuHover: string;
  menuUserBg: string;
  menuUserBorder: string;
  menuText: string;
  menuTextSecondary: string;
  sectionBg: string;
  sectionBorder: string;
  sectionAccent: string;
  link: string;
  linkHover: string;
  overlay: string;
  success: string;
  warning: string;
  error: string;
  info: string;
}

export interface Theme {
  mode: 'light' | 'dark';
  spacing: typeof spacing;
  typography: typeof typography;
  radius: typeof radius;
  breakpoints: typeof breakpoints;
  shadows: typeof shadows;
  colors: ThemeColors;
}

export const lightTheme: Theme = {
  mode: 'light',
  spacing,
  typography,
  radius,
  breakpoints,
  shadows,
  colors: {
    primary: '#1a365d',
    primaryHover: '#2a4a7f',
    accent: '#d4a843',
    accentHover: '#b8922e',

    background: '#ffffff',
    backgroundAlt: '#f8f9fa',
    backgroundElevated: '#ffffff',
    surface: '#f8fafc',
    surfaceHover: '#f1f5f9',

    text: '#1a202c',
    textSecondary: '#64748b',
    textMuted: '#94a3b8',
    textInverse: '#ffffff',

    border: '#e2e8f0',
    borderLight: 'rgba(0, 0, 0, 0.06)',
    divider: '#e2e8f0',

    headerBg: '#ffffff',
    headerBorder: 'rgba(0, 0, 0, 0.06)',
    headerShadow: '0 1px 8px rgba(0, 0, 0, 0.07)',

    searchBg: '#f8f9fa',
    searchBorder: '#e2e8f0',
    searchFocusBg: '#ffffff',

    footerBgStart: '#f8fafc',
    footerBgEnd: '#edf2f7',
    footerBorder: '#e2e8f0',
    footerText: '#64748b',
    footerHeading: '#1a365d',
    footerCopyright: '#94a3b8',

    cardBg: '#ffffff',
    cardBorder: 'rgba(0, 0, 0, 0.125)',

    inputBg: '#ffffff',
    inputBorder: '#d1d5db',
    inputText: '#1a202c',

    menuBg: '#ffffff',
    menuBorder: '#eeeeee',
    menuHover: '#f7f7f7',
    menuUserBg: '#fafafa',
    menuUserBorder: '#f0f0f0',
    menuText: '#333333',
    menuTextSecondary: '#888888',

    sectionBg: '#f8f7f3',
    sectionBorder: '#e0d8c8',
    sectionAccent: '#8b7d65',

    link: '#2d3e50',
    linkHover: '#1a365d',

    overlay: 'rgba(0, 0, 0, 0.5)',
    success: '#2e7d32',
    warning: '#ffc107',
    error: '#d32f2f',
    info: '#1976d2',
  },
};

export const darkTheme: Theme = {
  mode: 'dark',
  spacing,
  typography,
  radius,
  breakpoints,
  shadows: darkShadows,
  colors: {
    primary: '#90caf9',
    primaryHover: '#bbdefb',
    accent: '#ffd54f',
    accentHover: '#ffecb3',

    background: '#121212',
    backgroundAlt: '#1e1e1e',
    backgroundElevated: '#242424',
    surface: '#1e1e1e',
    surfaceHover: '#2a2a2a',

    text: '#e0e0e0',
    textSecondary: '#a0a0a0',
    textMuted: '#717171',
    textInverse: '#121212',

    border: '#333333',
    borderLight: 'rgba(255, 255, 255, 0.08)',
    divider: '#333333',

    headerBg: '#1a1a1a',
    headerBorder: 'rgba(255, 255, 255, 0.08)',
    headerShadow: '0 1px 8px rgba(0, 0, 0, 0.3)',

    searchBg: '#2a2a2a',
    searchBorder: '#404040',
    searchFocusBg: '#333333',

    footerBgStart: '#1a1a1a',
    footerBgEnd: '#141414',
    footerBorder: '#333333',
    footerText: '#a0a0a0',
    footerHeading: '#90caf9',
    footerCopyright: '#616161',

    cardBg: '#1e1e1e',
    cardBorder: 'rgba(255, 255, 255, 0.12)',

    inputBg: '#2a2a2a',
    inputBorder: '#404040',
    inputText: '#e0e0e0',

    menuBg: '#242424',
    menuBorder: '#333333',
    menuHover: '#333333',
    menuUserBg: '#1e1e1e',
    menuUserBorder: '#333333',
    menuText: '#e0e0e0',
    menuTextSecondary: '#a0a0a0',

    sectionBg: '#1e1e1e',
    sectionBorder: '#333333',
    sectionAccent: '#c4b896',

    link: '#90caf9',
    linkHover: '#bbdefb',

    overlay: 'rgba(0, 0, 0, 0.7)',
    success: '#66bb6a',
    warning: '#ffa726',
    error: '#ef5350',
    info: '#42a5f5',
  },
};

export interface ThemePreferenceContextValue {
  preference: ThemePreference;
  resolvedMode: ResolvedThemeMode;
  setPreference: (pref: ThemePreference) => void;
}

const ThemePreferenceContext = createContext<ThemePreferenceContextValue>({
  preference: 'system',
  resolvedMode: 'light',
  setPreference: () => {},
});

export function useThemePreference(): ThemePreferenceContextValue {
  return useContext(ThemePreferenceContext);
}

function getSystemMode(): ResolvedThemeMode {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function getStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // ignore
  }
  return 'system';
}

// Stable defaults so server and first client render match (avoids hydration mismatch).
const SSR_DEFAULT_PREFERENCE: ThemePreference = 'system';
const SSR_DEFAULT_SYSTEM_MODE: ResolvedThemeMode = 'light';

/** Initial theme state passed from SSR or read from client storage. */
export interface InitialThemeState {
  preference?: ThemePreference;
  systemMode?: ResolvedThemeMode;
}

declare global {
  interface Window {
    __INITIAL_THEME__?: InitialThemeState;
  }
}

function getInitialTheme(initialTheme?: InitialThemeState | null): {
  preference: ThemePreference;
  systemMode: ResolvedThemeMode;
} {
  if (typeof window !== 'undefined') {
    if (window.__INITIAL_THEME__) {
      return {
        preference: window.__INITIAL_THEME__.preference ?? SSR_DEFAULT_PREFERENCE,
        systemMode: window.__INITIAL_THEME__.systemMode ?? SSR_DEFAULT_SYSTEM_MODE,
      };
    }
    // Client without SSR payload (e.g. dev): read from storage so first paint matches preference
    return {
      preference: getStoredPreference(),
      systemMode: getSystemMode(),
    };
  }
  return {
    preference: initialTheme?.preference ?? SSR_DEFAULT_PREFERENCE,
    systemMode: initialTheme?.systemMode ?? SSR_DEFAULT_SYSTEM_MODE,
  };
}

function setResolvedCookie(resolvedMode: ResolvedThemeMode): void {
  try {
    document.cookie = `${RESOLVED_COOKIE}=${resolvedMode}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
  } catch {
    // ignore
  }
}

export interface ThemeManagerProps {
  children: React.ReactNode;
  initialTheme?: InitialThemeState | null;
}

export function ThemeManager({ children, initialTheme }: ThemeManagerProps): React.ReactElement {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    () => getInitialTheme(initialTheme).preference
  );
  const [systemMode, setSystemMode] = useState<ResolvedThemeMode>(
    () => getInitialTheme(initialTheme).systemMode
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setPreferenceState(getStoredPreference());
    setSystemMode(getSystemMode());
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent): void =>
      setSystemMode(e.matches ? 'dark' : 'light');
    mql.addEventListener?.('change', handler);
    return () => mql.removeEventListener?.('change', handler);
  }, []);

  const setPreference = useCallback((pref: ThemePreference) => {
    setPreferenceState(pref);
    try {
      localStorage.setItem(STORAGE_KEY, pref);
    } catch {
      // ignore
    }
  }, []);

  const resolvedMode: ResolvedThemeMode =
    preference === 'system' ? systemMode : preference;
  const theme = resolvedMode === 'dark' ? darkTheme : lightTheme;

  // Persist resolved theme in a cookie so SSR can render the same theme on next request (avoids flicker).
  useEffect(() => {
    if (typeof window !== 'undefined') setResolvedCookie(resolvedMode);
  }, [resolvedMode]);

  const contextValue = useMemo<ThemePreferenceContextValue>(
    () => ({
      preference,
      resolvedMode,
      setPreference,
    }),
    [preference, resolvedMode, setPreference]
  );

  return (
    <ThemePreferenceContext.Provider value={contextValue}>
      <SCThemeProvider theme={theme}>
        {children}
      </SCThemeProvider>
    </ThemePreferenceContext.Provider>
  );
}
